import { useState } from 'react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { X, Info } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { useQuery } from '@tanstack/react-query'
import { USER_QUERY_KEYS } from '@/api/queryKeys'
import { getRoles } from '@/api/userApi'
import type { RoleData } from '@/utils/helper'

interface AssignRoleDrawerProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly onConfirm: (role: string) => void
  readonly selectedCount: number
  readonly selectedUsers?: Array<{
    readonly id: string
    readonly name: string
    readonly avatar?: string
  }>
}

export function AssignRoleDrawer({
  open,
  onOpenChange,
  onConfirm,
  selectedCount,
  selectedUsers = [],
}: AssignRoleDrawerProps) {
  const [selectedRole, setSelectedRole] = useState('')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const handleConfirm = () => {
    if (selectedRole) {
      setShowConfirmDialog(true)
    }
  }

  const handleConfirmAssign = () => {
    onConfirm(selectedRole)
    setSelectedRole('')
    setShowConfirmDialog(false)
  }

  const handleClose = () => {
    setSelectedRole('')
    setShowConfirmDialog(false)
    onOpenChange(false)
  }

  const { data: rolesData } = useQuery({
    queryKey: USER_QUERY_KEYS.roles(),
    queryFn: getRoles,
  })

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} direction="right">
        <DrawerContent className="h-full w-full md:w-[400px] bg-white rounded-none md:rounded-xl">
          {/* Header */}
          <DrawerHeader className="border-b border-ss-neutral-50 p-4">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-lg font-semibold text-ss-text-primary">
                Assign Role to Selected Users
              </DrawerTitle>
              <DrawerClose asChild>
                <button className="p-1 rounded-lg hover:bg-ss-neutral-25 transition-colors">
                  <X className="h-5 w-5 text-ss-neutral-400" />
                </button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          {/* Content */}
          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
            {/* Description */}
            <p className="text-sm text-ss-text-secondary leading-relaxed">
              You have selected {selectedCount} Users. Choose a role from the
              list below to assign. This action will update the role for all
              selected users.
            </p>

            {/* Selected Users */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-ss-text-primary">
                Selected Users
              </h3>
              <div className="border border-ss-neutral-50 rounded-lg max-h-80 overflow-y-auto">
                {selectedUsers.map((user, index) => (
                  <div
                    key={user.id || index}
                    className={`flex items-center gap-3 py-2 px-3  ${
                      index < selectedUsers.length - 1
                        ? 'border-b border-ss-neutral-50'
                        : ''
                    }`}
                  >
                    <Avatar
                      className="h-8 w-8 rounded-lg"
                      aria-label={`Avatar for ${user.name}`}
                      tabIndex={0}
                    >
                      <AvatarImage
                        src={user.avatar}
                        alt={user.name || 'User avatar'}
                      />
                      <AvatarFallback className="rounded-lg">
                        {user.name ? user.name[0] : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-ss-text-secondary">
                      {user.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <Label
                htmlFor="role"
                className="text-sm font-semibold text-ss-text-primary"
              >
                User Role
              </Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger
                  className="w-full h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-900 focus:ring-ss-blue-900"
                  id="role"
                  aria-label="Select user role"
                >
                  <SelectValue placeholder="Employee" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(rolesData?.data) ? (
                    rolesData?.data?.map((role: RoleData) => (
                      <SelectItem
                        key={role.id}
                        value={role.id.toString()}
                        aria-label={role.name}
                      >
                        {role.title}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No roles found
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer */}
          <DrawerFooter className="border-t border-ss-neutral-50 bg-white p-6 rounded-bl-2xl rounded-br-2xl shadow-lg">
            <div className="flex items-center justify-between w-full">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="px-4 py-2 h-9 border-ss-neutral-200 text-ss-neutral-950 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={!selectedRole}
              >
                Assign Role
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="bg-ss-warning-100 rounded-full w-10 h-10 flex items-center justify-center mb-2">
              <Info className="h-6 w-6 text-ss-warning-200" strokeWidth={1.5} />
            </div>
            <DialogTitle className="text-lg font-semibold text-ss-neutral-950">
              Are you sure you want to assign role?
            </DialogTitle>
            <DialogDescription className="text-sm font-normal text-ss-neutral-600">
              Assigning a new role will override current roles for all selected
              users.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-25"
            >
              No
            </Button>
            <Button
              type="button"
              onClick={handleConfirmAssign}
              className="bg-ss-blue-900 text-white hover:bg-ss-blue-800"
            >
              Yes, Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
